import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const chipTones = {
  emerald: 'from-emerald-500/25 to-emerald-500/[0.06] text-emerald-400 border-emerald-500/25',
  blue: 'from-blue-500/25 to-blue-500/[0.06] text-blue-400 border-blue-500/25',
  green: 'from-green-500/25 to-green-500/[0.06] text-green-400 border-green-500/25',
  purple: 'from-purple-500/25 to-purple-500/[0.06] text-purple-400 border-purple-500/25',
  amber: 'from-amber-500/25 to-amber-500/[0.06] text-amber-400 border-amber-500/25',
  red: 'from-red-500/25 to-red-500/[0.06] text-red-400 border-red-500/25',
  neutral: 'from-white/10 to-white/[0.02] text-text-secondary border-white/10',
} as const;

const chipSizes = {
  sm: 'h-8 w-8 rounded-md [&_svg]:h-4 [&_svg]:w-4',
  md: 'h-10 w-10 rounded-lg [&_svg]:h-5 [&_svg]:w-5',
  lg: 'h-12 w-12 rounded-lg [&_svg]:h-6 [&_svg]:w-6',
} as const;

export type IconChipTone = keyof typeof chipTones;

export interface IconChipProps {
  icon: LucideIcon;
  tone?: IconChipTone;
  size?: keyof typeof chipSizes;
  className?: string;
}

/**
 * Branded icon presentation: a gradient tone chip with a top inner highlight
 * so lucide glyphs read as custom Opus iconography without changing the glyphs.
 */
export function IconChip({ icon: Icon, tone = 'emerald', size = 'lg', className }: IconChipProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center border bg-gradient-to-b',
        'shadow-[0_1px_2px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.1)]',
        chipTones[tone],
        chipSizes[size],
        className,
      )}
    >
      <Icon aria-hidden />
    </div>
  );
}
